import { format } from "date-fns";
import { ChevronLeft, ChevronRight, Loader2, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Drawer } from "@mantine/core";
import { Button } from "../../../../../components/ui/button";
import { Badge } from "../../../../../components/ui/badge";
import { useAllAutomations, useAutomationLogs } from "../../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import SignalDocumentsPanel from "../../signal-documents/SignalDocumentsPanel";

type SignalLogsTableProps = {
  alert: string;
  status: string;
};

const formatTimestamp = (value?: string) => {
  if (!value) return "-";
  try {
    return format(new Date(value), "MMM d, yyyy h:mm a");
  } catch {
    return value;
  }
};

const getStatusTone = (value?: string) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "success") return "default" as const;
  if (normalized === "error" || normalized === "failed") return "destructive" as const;
  return "secondary" as const;
};

const SignalLogsTable = ({ alert, status }: SignalLogsTableProps) => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const [page, setPage] = useState(0);
  const [drawerRunId, setDrawerRunId] = useState<string | null>(null);

  const logsQuery: any = useAutomationLogs({
    workspaceId: workspace?.id,
    automationType: "signal",
    status: status === "all" ? undefined : status,
    automationId: alert === "all" ? undefined : alert,
    enabled: !!workspace?.id,
    page,
  });

  const automationsQuery: any = useAllAutomations({
    workspaceId: workspace?.id,
    automationType: "signal",
    enabled: !!workspace?.id,
  });

  useEffect(() => {
    setPage(0);
  }, [alert, status]);

  const automationNameById = useMemo(() => {
    const map: Record<string, string> = {};
    (automationsQuery?.data || []).forEach((automation: any) => {
      if (automation?.id) map[automation.id] = automation.name || "Untitled";
    });
    return map;
  }, [automationsQuery?.data]);

  const logs = useMemo(() => {
    const source = logsQuery?.data?.logs || [];
    return source.map((item: any) => {
      const extraInfo = item?.extra_information || {};
      return {
        id: item?.id || `${item?.created_at}-${item?.automation_id}`,
        signalRunId: item?.signal_run_id || item?.id,
        createdAt: item?.created_at,
        automationName: item?.automation_name
          || automationNameById[item?.automation_id]
          || "Unknown signal",
        status: item?.status || "unknown",
        recordsAffected: extraInfo?.records_affected,
        documentsCount: extraInfo?.documents_count || 0,
        message: item?.message || extraInfo?.message || "",
      };
    });
  }, [logsQuery?.data?.logs, automationNameById]);

  const total = Number(logsQuery?.data?.total || 0);
  const pageSize = 20;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const isFirstPage = page <= 0;
  const isLastPage = page >= totalPages - 1;

  if (logsQuery?.isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (logsQuery?.isError) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Unable to load signal logs. Please try again.
        </div>
      </div>
    );
  }

  if (!logs.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 py-16">
        <p className="text-sm font-medium text-foreground">No logs found</p>
        <p className="text-xs text-muted-foreground">Try changing your filters.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full">
          <thead className="sticky top-0 z-10 bg-muted/70 backdrop-blur">
            <tr className="border-b border-border">
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground">Timestamp</th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground">Signal</th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground">Records</th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground">Documents</th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground">Message</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log: any) => (
              <tr key={log.id} className="border-b border-border text-sm hover:bg-muted/40">
                <td className="px-6 py-3 text-muted-foreground">{formatTimestamp(log.createdAt)}</td>
                <td className="px-6 py-3 text-foreground">{log.automationName}</td>
                <td className="px-6 py-3">
                  <Badge variant={getStatusTone(log.status)} className="capitalize">
                    {String(log.status || "unknown")}
                  </Badge>
                </td>
                <td className="px-6 py-3 text-foreground">{log.recordsAffected ?? "-"}</td>
                <td className="px-6 py-3">
                  {log.documentsCount > 0 ? (
                    <Badge
                      variant="secondary"
                      className="cursor-pointer gap-1 bg-purple-100 text-purple-700 hover:bg-purple-200"
                      onClick={() => setDrawerRunId(log.signalRunId)}
                    >
                      <Sparkles className="h-3 w-3" />
                      {log.documentsCount}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
                <td className="px-6 py-3 text-muted-foreground">{log.message || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-border px-6 py-3">
        <span className="text-xs text-muted-foreground">
          Page {page + 1} of {totalPages}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((prev) => Math.max(0, prev - 1))}
            disabled={isFirstPage}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={isLastPage}
            className="gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Drawer
        opened={!!drawerRunId}
        onClose={() => setDrawerRunId(null)}
        title="AI Documents"
        position="right"
        size="lg"
      >
        {drawerRunId && workspace?.id && (
          <SignalDocumentsPanel
            signalRunId={drawerRunId}
            workspaceId={workspace.id}
          />
        )}
      </Drawer>
    </div>
  );
};

export default SignalLogsTable;
